package io.archisource.frontend

import io.archisource.shared.CurrentUserDto
import io.archisource.shared.ContentRevisionDto
import io.archisource.shared.PageContentDto
import io.archisource.shared.PageContentKindDto
import io.archisource.shared.PageSummaryDto
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.await
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import react.FC
import react.Props
import react.create
import react.dom.client.createRoot
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.aside
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.header
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.small
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.ul
import react.useEffect
import react.useEffectOnce
import react.useState
import web.dom.ElementId
import web.dom.document
import web.cssom.ClassName
import web.form.FormMethod
import web.form.post
import web.html.HtmlTagName.form as formTag
import web.http.Response
import web.http.text
import web.storage.sessionStorage
import web.window.window

@JsModule("@babel/standalone")
@JsNonModule
private external val Babel: dynamic

@JsModule("react")
@JsNonModule
private external val ReactModule: dynamic

@JsModule("react-dom/client")
@JsNonModule
private external val ReactDomClient: dynamic

private val json = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
}
private const val SKIP_AUTOMATIC_LOGIN = "archisource.skipAutomaticLogin"
private const val JSX_HOST_ID = "jsx-page-host"
private const val CONTENT_REFRESH_INTERVAL_MS = 5000
private var mountedBrowserRoot: dynamic = null
private var mountedBrowserHost: dynamic = null
private var componentRuntimeSource: String? = null
private var componentLibrarySource: String? = null
private var componentLibraryRevision: Long = -1L
private var browserMountGeneration: Long = 0L

@Serializable
private data class ComponentLibraryManifest(
    val runtime: List<String>,
    val components: List<String>,
)

private data class ComponentLibrarySource(
    val runtime: String,
    val full: String,
)

private val App = FC<Props> {
    var currentUser by useState<CurrentUserDto?>(null)
    var pages by useState(emptyList<PageSummaryDto>())
    var expandedPageFolders by useState(emptySet<String>())
    var pageSearch by useState("")
    var currentPage by useState<PageContentDto?>(null)
    var requestedPath by useState(window.location.pathname.removeSuffix("/").ifBlank { "/" })
    var contentRevision by useState(0L)
    var sessionLoading by useState(true)
    var pageLoading by useState(false)
    var error by useState<String?>(null)
    var navigationCollapsed by useState(false)
    var mobileNavigationOpen by useState(false)

    useEffectOnce {
        try {
            val skipAutomaticLogin = sessionStorage.getItem(SKIP_AUTOMATIC_LOGIN) != null
            sessionStorage.removeItem(SKIP_AUTOMATIC_LOGIN)
            val userResponse = sessionFetch("/api/me")
            if (!userResponse.ok) error("Current-user request failed with ${userResponse.status}")
            val user = json.decodeFromString<CurrentUserDto>(userResponse.text())
            currentUser = user
            sessionLoading = false
            if (!user.authenticated && !skipAutomaticLogin) {
                window.location.href = "/auth/login"
                return@useEffectOnce
            }
            if (!user.authenticated) return@useEffectOnce

            val pagesResponse = sessionFetch("/api/pages")
            if (!pagesResponse.ok) error("Page catalog request failed with ${pagesResponse.status}")
            val registeredPages = json.decodeFromString<List<PageSummaryDto>>(pagesResponse.text())
            pages = registeredPages
            startContentPolling(
                onRevision = { revision -> contentRevision = revision },
                onPages = { refreshedPages -> pages = refreshedPages },
            )
        } catch (failure: Throwable) {
            sessionLoading = false
            pageLoading = false
            error = failure.message ?: "ArchiSource could not load the application."
        }
    }

    useEffect(currentUser, pages, requestedPath) {
        if (sessionLoading || currentUser?.authenticated != true) return@useEffect

        val selected = if (requestedPath == "/") {
            pages.firstOrNull()
        } else {
            pages.firstOrNull { it.path == requestedPath }
        }
        if (selected == null) {
            currentPage = null
            error = when {
                pages.isEmpty() -> "No pages are registered. Add an XML, JSX, or Markdown file to the pages directory."
                requestedPath != "/" -> "No page is registered for $requestedPath."
                else -> null
            }
            return@useEffect
        }

        pageLoading = true
        currentPage = null
        error = null
        try {
            val pageResponse = sessionFetch("/api/page?path=${encodeUriComponent(selected.path)}")
            if (!pageResponse.ok) error("Page request failed with ${pageResponse.status}")
            currentPage = json.decodeFromString<PageContentDto>(pageResponse.text())
        } catch (failure: Throwable) {
            error = failure.message ?: "ArchiSource could not load the page."
        } finally {
            pageLoading = false
        }
    }

    useEffect(currentPage, contentRevision) {
        val loadedPage = currentPage ?: return@useEffect
        if (contentRevision == 0L) return@useEffect
        try {
            val response = sessionFetch("/api/page?path=${encodeUriComponent(loadedPage.path)}")
            if (!response.ok) return@useEffect
            val latest = json.decodeFromString<PageContentDto>(response.text())
            if (latest != loadedPage) currentPage = latest
        } catch (_: Throwable) {
            // The currently rendered page remains usable if a poll races shutdown.
        }
    }

    useEffect(currentPage, currentUser) {
        val loadedPage = currentPage ?: return@useEffect
        val user = currentUser?.takeIf(CurrentUserDto::authenticated) ?: return@useEffect
        val generation = ++browserMountGeneration
        val mountError = when (loadedPage.kind) {
            PageContentKindDto.JSX -> mountJsxPage(loadedPage, user, contentRevision, generation)
            PageContentKindDto.UI -> mountExternalPage(loadedPage, contentRevision, generation)
            else -> null
        }
        if (generation == browserMountGeneration && mountError != null) error = mountError
    }

    useEffect(mobileNavigationOpen) {
        if (!mobileNavigationOpen) return@useEffect
        val closeOnEscape: (dynamic) -> Unit = { event ->
            if (event.key == "Escape") mobileNavigationOpen = false
        }
        window.asDynamic().addEventListener("keydown", closeOnEscape)
        try {
            awaitCancellation()
        } finally {
            window.asDynamic().removeEventListener("keydown", closeOnEscape)
        }
    }

    div {
        className = ClassName(
            if (navigationCollapsed) "application-shell navigation-collapsed" else "application-shell",
        )

        header {
            className = ClassName("top-bar")
            div {
                className = ClassName("top-bar-leading")
                button {
                    className = ClassName("menu-toggle desktop-menu-toggle")
                    ariaLabel = if (navigationCollapsed) "Expand main menu" else "Collapse main menu"
                    ariaExpanded = !navigationCollapsed
                    ariaControls = ElementId("main-navigation")
                    title = if (navigationCollapsed) "Expand main menu" else "Collapse main menu"
                    onClick = { navigationCollapsed = !navigationCollapsed }
                }
                button {
                    className = ClassName("menu-toggle mobile-menu-toggle")
                    ariaLabel = if (mobileNavigationOpen) "Close main menu" else "Open main menu"
                    ariaExpanded = mobileNavigationOpen
                    ariaControls = ElementId("main-navigation")
                    title = if (mobileNavigationOpen) "Close main menu" else "Open main menu"
                    onClick = { mobileNavigationOpen = !mobileNavigationOpen }
                }
                div {
                    className = ClassName("brand")
                    span { className = ClassName("brand-mark"); +"A" }
                    span { +"ArchiSource" }
                }
            }
            div {
                className = ClassName("session-area")
                when {
                    sessionLoading -> small { +"Checking session…" }
                    currentUser?.authenticated == true -> {
                        val user = requireNotNull(currentUser)
                        div {
                            className = ClassName("user-summary")
                            span { +(user.displayName ?: user.username ?: "Authenticated user") }
                            small {
                                val details = listOfNotNull(
                                    user.username?.takeUnless { it == user.displayName },
                                    user.email,
                                    user.roles.takeIf(Set<String>::isNotEmpty)
                                        ?.let { "${it.size} role${if (it.size == 1) "" else "s"}" },
                                    user.permissions.takeIf(Set<String>::isNotEmpty)
                                        ?.let { "${it.size} permission${if (it.size == 1) "" else "s"}" },
                                )
                                +details.joinToString(" · ")
                            }
                        }
                        button {
                            className = ClassName("secondary-button")
                            onClick = { signOut() }
                            +"Log out"
                        }
                    }

                    else -> button {
                        className = ClassName("primary-button")
                        onClick = { window.location.href = "/auth/login" }
                        +"Log in"
                    }
                }
            }
        }

        if (mobileNavigationOpen) {
            button {
                className = ClassName("navigation-backdrop")
                ariaLabel = "Close main menu"
                onClick = { mobileNavigationOpen = false }
            }
        }

        aside {
            id = ElementId("main-navigation")
            className = ClassName(if (mobileNavigationOpen) "sidebar mobile-open" else "sidebar")
            div {
                className = ClassName("sidebar-heading")
                div {
                    className = ClassName("page-search")
                    input {
                        value = pageSearch
                        placeholder = "Search pages…"
                        ariaLabel = "Search pages"
                        onChange = { event -> pageSearch = event.target.value }
                    }
                    if (pageSearch.isNotBlank()) {
                        val query = pageSearch.trim().lowercase()
                        val hits = pages.filter { page ->
                            page.title.lowercase().contains(query) || page.path.lowercase().contains(query)
                        }.sortedWith(compareBy<PageSummaryDto>({ !it.title.lowercase().startsWith(query) }, { it.title.lowercase() }))
                        div {
                            className = ClassName("page-search-results")
                            if (hits.isEmpty()) {
                                small { +"No matching pages" }
                            } else {
                                hits.take(MAX_PAGE_SEARCH_RESULTS).forEach { page ->
                                    button {
                                        className = ClassName("page-search-result")
                                        onClick = {
                                            requestedPath = page.path
                                            expandedPageFolders = expandedPageFolders + pageParentFolders(page.path)
                                            pageSearch = ""
                                            mobileNavigationOpen = false
                                            window.asDynamic().history.pushState(null, "", page.path)
                                        }
                                        span { +page.title }
                                        small { +page.path }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            nav {
                className = ClassName("page-navigation")
                if (pages.isEmpty()) {
                    p { className = ClassName("empty-navigation"); +"No pages" }
                } else {
                    ul {
                        flattenPageNavigation(pages, expandedPageFolders).forEach { node ->
                            li {
                                if (node.folder) {
                                    button {
                                        className = ClassName("page-folder")
                                        style = pageIndentStyle("${10 + node.depth * 14}px")
                                        onClick = {
                                            expandedPageFolders = if (expandedPageFolders.contains(node.path)) {
                                                expandedPageFolders - node.path
                                            } else {
                                                expandedPageFolders + node.path
                                            }
                                        }
                                        span { +node.title }
                                        small {
                                            className = ClassName("entity-count-badge")
                                            +node.entityCount.toString()
                                        }
                                    }
                                } else {
                                    val page = requireNotNull(node.page)
                                    a {
                                        href = page.path
                                        onClick = { event ->
                                            event.preventDefault()
                                            requestedPath = page.path
                                            mobileNavigationOpen = false
                                            window.asDynamic().history.pushState(null, "", page.path)
                                        }
                                        className = ClassName(if (page.path == currentPage?.path || page.path == requestedPath) "active" else "")
                                        style = pageIndentStyle("${10 + node.depth * 14}px")
                                        span { +page.title }
                                        small { +page.path }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        main {
            className = ClassName("content-panel")
            when {
                sessionLoading -> statusPanel("Loading ArchiSource…")
                currentUser?.authenticated != true -> {
                    div {
                        className = ClassName("status-panel")
                        p { +"Log in to view the registered ArchiSource pages." }
                        button {
                            className = ClassName("primary-button")
                            onClick = { window.location.href = "/auth/login" }
                            +"Log in"
                        }
                    }
                }

                pageLoading -> statusPanel("Loading page…")
                error != null -> statusPanel(requireNotNull(error), isError = true)
                currentPage == null -> statusPanel("Select a page from the navigation.")
                currentPage?.kind == PageContentKindDto.UI -> div {
                    id = ElementId(JSX_HOST_ID)
                    className = ClassName("page-surface metadata-page")
                }

                currentPage?.kind == PageContentKindDto.REST_API -> div {
                    className = ClassName("status-panel")
                    p { +"This definition exposes bulk CRUD at /api/rest${requireNotNull(currentPage).path}." }
                }

                currentPage?.kind == PageContentKindDto.HTML -> div {
                    className = ClassName("page-surface markdown-page")
                    val innerHtml: dynamic = js("({})")
                    innerHtml.__html = requireNotNull(currentPage).content.orEmpty()
                    dangerouslySetInnerHTML = innerHtml
                }

                else -> div {
                    id = ElementId(JSX_HOST_ID)
                    className = ClassName("page-surface browser-page")
                }
            }
        }
    }
}

private data class PageNavigationNode(
    val path: String,
    val title: String,
    val depth: Int,
    val folder: Boolean,
    val expanded: Boolean = false,
    val entityCount: Int = 0,
    val page: PageSummaryDto? = null,
)

private fun pageParentFolders(path: String): Set<String> {
    val segments = path.trim('/').split('/').filter(String::isNotBlank)
    if (segments.size < 2) return emptySet()
    return (1 until segments.size).mapTo(linkedSetOf()) { index -> "/${segments.take(index).joinToString("/")}" }
}

private fun flattenPageNavigation(
    pages: List<PageSummaryDto>,
    expanded: Set<String>,
): List<PageNavigationNode> {
    val folders = linkedSetOf<String>()
    pages.forEach { page ->
        val parts = page.path.trim('/').split('/').filter(String::isNotBlank)
        for (index in 1 until parts.size) folders += "/${parts.take(index).joinToString("/")}"
    }
    fun title(path: String) = path.substringAfterLast('/').replace('-', ' ').replace('_', ' ')
        .replaceFirstChar { it.uppercase() }
    fun children(parent: String): List<PageNavigationNode> {
        val folderChildren = folders.filter { it.substringBeforeLast('/', "") == parent }
            .map { path ->
                val entityCount = folders.count { it.substringBeforeLast('/', "") == path } +
                    pages.count { it.path.substringBeforeLast('/', "") == path }
                PageNavigationNode(
                    path = path,
                    title = title(path),
                    depth = path.count { it == '/' } - 1,
                    folder = true,
                    expanded = path in expanded,
                    entityCount = entityCount,
                )
            }
        val pageChildren = pages.filter { it.path.substringBeforeLast('/', "") == parent }
            .map { page -> PageNavigationNode(page.path, page.title, page.path.count { it == '/' } - 1, false, page = page) }
        return (folderChildren + pageChildren).sortedWith(compareBy<PageNavigationNode>({ !it.folder }, { it.title.lowercase() }))
    }
    val result = mutableListOf<PageNavigationNode>()
    fun collect(parent: String) {
        children(parent).forEach { node ->
            result += node
            if (node.folder && node.expanded) collect(node.path)
        }
    }
    collect("")
    return result
}

private const val MAX_PAGE_SEARCH_RESULTS = 30

private fun react.ChildrenBuilder.statusPanel(message: String, isError: Boolean = false) {
    div {
        className = ClassName(if (isError) "status-panel error" else "status-panel")
        p { +message }
    }
}

private fun signOut() {
    sessionStorage.setItem(SKIP_AUTOMATIC_LOGIN, "true")
    val logoutForm = document.createElement(formTag)
    logoutForm.method = FormMethod.post
    logoutForm.action = "/auth/logout"
    document.body.appendChild(logoutForm)
    logoutForm.submit()
}

private fun startContentPolling(
    onRevision: (Long) -> Unit,
    onPages: (List<PageSummaryDto>) -> Unit,
) {
    var lastRevision = 0L
    lateinit var poll: () -> Unit
    fun schedule() {
        window.asDynamic().setTimeout(poll, CONTENT_REFRESH_INTERVAL_MS)
    }
    poll = {
        val revisionRequest: dynamic = browserFetch("/api/content-revision", sessionFetchOptions)
        revisionRequest.then({ response: dynamic ->
            if (!response.ok) {
                schedule()
            } else {
                response.text().then({ body: dynamic ->
                    val revision = body.unsafeCast<String>().let { text ->
                        json.decodeFromString<ContentRevisionDto>(text).revision
                    }
                    if (revision != lastRevision) {
                        lastRevision = revision
                        val pagesRequest: dynamic = browserFetch("/api/pages?revision=$revision", sessionFetchOptions)
                        pagesRequest.then({ pagesResponse: dynamic ->
                            if (pagesResponse.ok) {
                                pagesResponse.text().then({ pagesBody: dynamic ->
                                    onPages(json.decodeFromString<List<PageSummaryDto>>(pagesBody.unsafeCast<String>()))
                                    onRevision(revision)
                                    refreshLibraryStyles(revision)
                                })
                            }
                            schedule()
                        }).catch({ schedule() })
                    } else {
                        schedule()
                    }
                }).catch({ schedule() })
            }
        }).catch({ schedule() })
    }
    schedule()
}

private fun refreshLibraryStyles(revision: Long) {
    val refresh: dynamic = js("""
        (function(revision) {
          var current = document.querySelector('link[href^="/library/styles.css"]');
          if (!current || !current.parentNode) return;
          var replacement = current.cloneNode(true);
          replacement.href = '/library/styles.css?v=' + revision;
          replacement.onload = function() {
            if (current.parentNode) current.parentNode.removeChild(current);
          };
          replacement.onerror = function() {
            if (replacement.parentNode) replacement.parentNode.removeChild(replacement);
          };
          current.parentNode.insertBefore(replacement, current.nextSibling);
        })
    """)
    refresh(revision)
}

private suspend fun mountJsxPage(page: PageContentDto, user: CurrentUserDto, revision: Long, generation: Long): String? = try {
    // JSX pages may use the same built-in widgets as declarative XML pages.
    // Previously only hooks were loaded here, which made components such as
    // DetailFieldEditor appear undefined to browser-side pages.
    val librarySource = loadComponentLibrary(revision).full
    val options: dynamic = js("({})")
    options.presets = arrayOf("react")
    val compiled = Babel.transform("$librarySource\n${requireNotNull(page.content)}", options).code.unsafeCast<String>()
    val functionConstructor: dynamic = js("Function")
    val factory = functionConstructor(
        "React",
        "ReactDOM",
        "pageContext",
        "$compiled\n" +
            "if (typeof Page !== 'function') throw new Error('A JSX page must define function Page(props)');\n" +
            "return React.createElement(ArchiSourceRuntimeProvider, { page: pageContext.page, user: pageContext.user }, " +
            "React.createElement(Page, pageContext));",
    )
    val context: dynamic = js("({})")
    context.path = page.path
    context.page = parseJson(json.encodeToString(page))
    context.reactDom = ReactDomClient
    context.user = jsUser(user)
    val element = factory(ReactModule, ReactDomClient, context)
    val host = requireNotNull(document.getElementById(ElementId(JSX_HOST_ID))) {
        "The JSX page host is not available"
    }
    if (generation != browserMountGeneration) return null
    ensureBrowserRoot(host)
    mountedBrowserRoot.render(element)
    null
} catch (failure: Throwable) {
    "JSX page '${page.title}' failed to compile: ${failure.message ?: "unknown error"}"
}

private suspend fun mountExternalPage(page: PageContentDto, revision: Long, generation: Long): String? = try {
    val source = loadComponentLibrary(revision).full
    val options: dynamic = js("({})")
    options.presets = arrayOf("react")
    val compiled = Babel.transform(source, options).code.unsafeCast<String>()
    val functionConstructor: dynamic = js("Function")
    val factory = functionConstructor(
        "React",
        "ReactDOM",
        "pageContext",
        "$compiled\n" +
            "if (typeof ArchiSourcePage !== 'function') " +
            "throw new Error('The external library must define function ArchiSourcePage(props)');\n" +
            "return React.createElement(ArchiSourcePage, pageContext);",
    )
    val context: dynamic = js("({})")
    context.page = parseJson(json.encodeToString(page))
    context.reactDom = ReactDomClient
    val element = factory(ReactModule, ReactDomClient, context)
    val host = requireNotNull(document.getElementById(ElementId(JSX_HOST_ID))) {
        "The metadata page host is not available"
    }
    if (generation != browserMountGeneration) return null
    ensureBrowserRoot(host)
    mountedBrowserRoot.render(element)
    null
} catch (failure: Throwable) {
    "External component library failed for '${page.title}': ${failure.message ?: "unknown error"}"
}

private suspend fun loadComponentLibrary(revision: Long): ComponentLibrarySource {
    if (componentRuntimeSource != null && componentLibrarySource != null && componentLibraryRevision == revision) {
        return ComponentLibrarySource(componentRuntimeSource.orEmpty(), componentLibrarySource.orEmpty())
    }
    val manifestResponse = sessionFetch("/library/runtime-manifest.json?v=$revision")
    if (!manifestResponse.ok) {
        error("External component manifest request failed with ${manifestResponse.status}")
    }
    val manifest = json.decodeFromString<ComponentLibraryManifest>(manifestResponse.text())
    require(manifest.runtime.isNotEmpty()) { "The external component manifest contains no runtime files" }
    require(manifest.components.isNotEmpty()) { "The external component manifest contains no component files" }
    suspend fun load(files: List<String>): String = buildString {
        files.forEach { file ->
            require(validLibraryFile(file)) { "Invalid external component path '$file'" }
            val response = sessionFetch("/library/$file?v=$revision")
            if (!response.ok) error("External component '$file' request failed with ${response.status}")
            append("\n/* library/$file */\n")
            append(response.text())
            append('\n')
        }
    }
    val runtime = load(manifest.runtime)
    val source = "$runtime\n${load(manifest.components)}"
    componentRuntimeSource = runtime
    componentLibrarySource = source
    componentLibraryRevision = revision
    return ComponentLibrarySource(runtime, source)
}

private fun validLibraryFile(path: String): Boolean =
    path.isNotBlank() &&
        !path.startsWith('/') &&
        path.split('/').none { it.isBlank() || it == "." || it == ".." } &&
        (path.endsWith(".js") || path.endsWith(".jsx"))

private fun ensureBrowserRoot(host: dynamic) {
    if (mountedBrowserRoot == null || mountedBrowserHost !== host) {
        mountedBrowserRoot?.unmount()
        mountedBrowserRoot = ReactDomClient.createRoot(host)
        mountedBrowserHost = host
    }
}

private fun jsUser(user: CurrentUserDto): dynamic {
    val value: dynamic = js("({})")
    value.authenticated = user.authenticated
    value.subject = user.subject
    value.applicationUserId = user.applicationUserId
    value.username = user.username
    value.displayName = user.displayName
    value.email = user.email
    value.givenName = user.givenName
    value.familyName = user.familyName
    value.roles = user.roles.toTypedArray()
    value.permissions = user.permissions.toTypedArray()
    val attributes: dynamic = js("({})")
    user.attributes.forEach { (name, values) -> attributes[name] = values.toTypedArray() }
    value.attributes = attributes
    return value
}

private val encodeUriComponent: (String) -> String = js("encodeURIComponent")
private val parseJson: dynamic = js("JSON.parse")
private val browserFetch: dynamic = js("fetch")
private val sessionFetchOptions: dynamic = js("({ credentials: 'same-origin', cache: 'no-store' })")

private suspend fun sessionFetch(url: String): Response =
    browserFetch(url, sessionFetchOptions).unsafeCast<kotlin.js.Promise<Response>>().await()
private val pageIndentStyle: (String) -> dynamic = js("function(value) { return { paddingLeft: value }; }")

fun main() {
    val root = requireNotNull(document.getElementById(ElementId("root"))) { "Missing #root element" }
    createRoot(root).render(App.create())
}
